import { useEffect, useMemo, type ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

import { useLocalization } from "@i18n";

import type { AdminStructureDetail } from "../../api/structures/structures.types";
import {
  createAdminStructureOpenPitGeometrySchema,
  mapDetailToOpenPitGeometryForm,
  type AdminStructureOpenPitGeometryForm,
} from "../../schemas";
import { AdminStructureOpenPitGeometryFormContext } from "./adminStructureOpenPitGeometryForm.context.definition";

export type AdminStructureOpenPitGeometryFormProviderProps = {
  detail: AdminStructureDetail;
  canWrite: boolean;
  typePersisted: boolean;
  children: ReactNode;
};

export const AdminStructureOpenPitGeometryFormProvider = ({
  detail,
  canWrite,
  typePersisted,
  children,
}: AdminStructureOpenPitGeometryFormProviderProps) => {
  const { translate } = useLocalization();
  const schema = useMemo(
    () => createAdminStructureOpenPitGeometrySchema(translate),
    [translate],
  );

  const form = useForm<AdminStructureOpenPitGeometryForm>({
    resolver: zodResolver(schema),
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: mapDetailToOpenPitGeometryForm(detail),
  });

  useEffect(() => {
    form.reset(mapDetailToOpenPitGeometryForm(detail));
  }, [detail, form]);

  return (
    <AdminStructureOpenPitGeometryFormContext.Provider
      value={{ form, canWrite, typePersisted }}
    >
      <FormProvider {...form}>{children}</FormProvider>
    </AdminStructureOpenPitGeometryFormContext.Provider>
  );
};
